import { getFullAgentContent, getFullSkillFiles, discoverPackageFiles, readPackageFile, getPackageVersion } from "./package-assets.js";
import { getCanonicalAgentName } from "./identity.js";

const COMMON_FILES: Record<string, string> = {
  "opencode/README.md": `# OpenCode Setup\n\nThis directory is managed by \`ai-workflow\` init.\nAdd agent and command files required by your project workflow.\n`
};

const FULL_PRIMARY_AGENTS: string[] = [
  "atlas",
  "nexus",
  "orion",
  "astra",
  "sage",
  "phoenix"
];

const FULL_SKILLS: string[] = [
  "project-memory",
  "optimize-tokens",
  "documentation",
  "architecture",
  "technical-leadership",
  "product-discovery",
  "product-planning",
  "spec-driven-development",
  "pr-workflow",
  "qa-workflow",
  "release-workflow",
  "deployment",
  "ui-ux-design",
  "design-principles",
  "frontend-development",
  "backend-development",
  "full-stack-development",
  "refactoring",
  "prompt-engineer"
];

function buildRuntimeFiles({ includeFormalEvidence = false }: { includeFormalEvidence?: boolean } = {}): Record<string, string> {
  const files: Record<string, string> = {};

  for (const agent of FULL_PRIMARY_AGENTS) {
    const content = getFullAgentContent(agent);
    if (content === null) {
      throw new Error(`CRITICAL FAILURE: Missing required agent asset: dist-assets/agents/${agent}.md`);
    }
    files[`opencode/agents/${agent}.md`] = content;
  }

  for (const skill of FULL_SKILLS) {
    const skillFiles = getFullSkillFiles(skill);
    if (Object.keys(skillFiles).length === 0) {
      continue;
    }
    for (const [relPath, content] of Object.entries(skillFiles)) {
      const targetPath = relPath.replace(/^dist-assets\//, "opencode/");
      if (!files[targetPath]) {
        files[targetPath] = content;
      }
    }
  }

  // Copy governance policy into opencode/docs/policies/ so the relative link
  // ../../docs/policies/SKILLS_COMMON_GOVERNANCE.md resolves correctly from
  // opencode/skills/{skill}/SKILL.md for OpenCode and all platform adapters.
  const opencodeGovernancePolicy = readPackageFile("dist-assets/docs/policies/SKILLS_COMMON_GOVERNANCE.md");
  if (opencodeGovernancePolicy !== null) {
    files["opencode/docs/policies/SKILLS_COMMON_GOVERNANCE.md"] = opencodeGovernancePolicy;
  }

  const commandFiles = discoverPackageFiles("dist-assets/commands");
  for (const [relPath, content] of Object.entries(commandFiles)) {
    const targetPath = relPath.replace(/^dist-assets\//, "opencode/");
    if (!files[targetPath]) {
      files[targetPath] = content;
    }
  }

  const opencodeSkillFiles = discoverPackageFiles("dist-assets/skills");
  for (const [relPath, content] of Object.entries(opencodeSkillFiles)) {
    const targetPath = relPath.replace(/^dist-assets\//, "opencode/");
    if (!files[targetPath]) {
      files[targetPath] = content;
    }
  }

  const harnessWorkflowFiles = discoverPackageFiles("dist-assets/harness/workflows");
  for (const [relPath, content] of Object.entries(harnessWorkflowFiles)) {
    const targetPath = relPath.replace(/^dist-assets\//, "");
    files[targetPath] = content;
  }

  const harnessHandoffFiles = discoverPackageFiles("dist-assets/harness/handoffs");
  for (const [relPath, content] of Object.entries(harnessHandoffFiles)) {
    const targetPath = relPath.replace(/^dist-assets\//, "");
    files[targetPath] = content;
  }

  const agentsContent = readPackageFile("dist-assets/AGENTS.md");
  if (agentsContent !== null) files["AGENTS.md"] = agentsContent;

  const consumerQuickstart = readPackageFile("dist-assets/docs/QUICKSTART.md");
  if (consumerQuickstart !== null) files["QUICKSTART.md"] = consumerQuickstart;

  const policyContent = readPackageFile("dist-assets/docs/architecture-policy.md");
  if (policyContent !== null) files["opencode/docs/architecture-policy.md"] = policyContent;

  const dpContent = readPackageFile("dist-assets/docs/design-patterns-policy.md");
  if (dpContent !== null) files["opencode/docs/design-patterns-policy.md"] = dpContent;

  const visualValidationGuide = readPackageFile("dist-assets/docs/visual-validation-guide.md");
  if (visualValidationGuide !== null) files["opencode/docs/visual-validation-guide.md"] = visualValidationGuide;

  const governancePolicies = discoverPackageFiles("dist-assets/docs/policies");
  for (const [relPath, content] of Object.entries(governancePolicies)) {
    const targetPath = relPath.replace(/^dist-assets\//, "opencode/");
    files[targetPath] = content;
  }

  const compatibilityDocs = discoverPackageFiles("dist-assets/docs/compatibility");
  for (const [relPath, content] of Object.entries(compatibilityDocs)) {
    const targetPath = relPath.replace(/^dist-assets\//, "opencode/");
    files[targetPath] = content;
  }

  const qualityReferenceFiles = discoverPackageFiles("dist-assets/docs/references");
  for (const [relPath, content] of Object.entries(qualityReferenceFiles)) {
    const targetPath = relPath.replace(/^dist-assets\//, "opencode/");
    files[targetPath] = content;
  }

  const workflowProfileFiles = discoverPackageFiles("dist-assets/docs/profiles");
  for (const [relPath, content] of Object.entries(workflowProfileFiles)) {
    const targetPath = relPath.replace(/^dist-assets\//, "opencode/");
    files[targetPath] = content;
  }

  const templateFiles = discoverPackageFiles("dist-assets/templates");
  for (const [relPath, content] of Object.entries(templateFiles)) {
    if (!includeFormalEvidence && relPath.includes("/owner-evidence/")) continue;
    const targetPath = relPath.replace(/^dist-assets\//, "");
    files[targetPath] = content;
  }

  const schemaFiles = discoverPackageFiles("dist-assets/schemas");
  for (const [relPath, content] of Object.entries(schemaFiles)) {
    const targetPath = relPath.replace(/^dist-assets\//, "");
    files[targetPath] = content;
  }

  return files;
}

function buildExtraFiles(): Record<string, string> {
  const files: Record<string, string> = {};
  const exampleFiles = discoverPackageFiles("dist-assets/examples");
  for (const [relPath, content] of Object.entries(exampleFiles)) {
    const targetPath = relPath.replace(/^dist-assets\//, "");
    files[targetPath] = content;
  }
  return files;
}

const STANDARD_FILES = buildRuntimeFiles();
const FULL_FILES = { ...buildRuntimeFiles({ includeFormalEvidence: true }), ...buildExtraFiles() };

export const PROFILE_FILES: Record<string, Record<string, string>> = {
  standard: {
    ...COMMON_FILES,
    ...STANDARD_FILES
  },
  full: {
    ...COMMON_FILES,
    ...FULL_FILES
  }
};

export function isValidProfile(profile: any): boolean {
  return profile === "standard" || profile === "full";
}

export function getTemplateFiles(profile: string = "standard"): Record<string, string> {
  return PROFILE_FILES[profile] ?? PROFILE_FILES.standard;
}

export function getManagedBlocks(): string[] {
  return [
    ...FULL_PRIMARY_AGENTS.map((agent) => `opencode.jsonc:agent.${getCanonicalAgentName(agent)}`),
    ...FULL_SKILLS.map((skill) => `opencode.jsonc:agent.${getCanonicalAgentName(skill)}`),
    "opencode.jsonc:command.atlas",
    "opencode.jsonc:command.run",
    "opencode.jsonc:command.discover",
    "opencode.jsonc:command.spec-create",
    "opencode.jsonc:command.spec-review",
    "opencode.jsonc:command.spec-implement",
    "opencode.jsonc:command.plan",
    "opencode.jsonc:command.implement",
    "opencode.jsonc:command.validate",
    "opencode.jsonc:command.audit",
    "opencode.jsonc:command.optimize-tokens",
    "opencode.jsonc:command.update-memory",
    "opencode.jsonc:command.release",
    "opencode.jsonc:command.deploy"
  ];
}

export interface BuildAiWorkflowConfigOptions {
  profile: string;
  runtimes?: string[];
  managedFiles?: any;
  managedLinks?: any[];
}

export interface AiWorkflowConfig {
  package: string;
  version: string | null;
  runtime: string;
  profile: string;
  installMode: string;
  generatedAt: string;
  paths: {
    agents: string;
    commands: string;
    skills: string;
    policies: string;
    harness: string;
    schemas: string;
  };
}

export function buildAiWorkflowConfig({ profile, runtimes = ["opencode"] }: BuildAiWorkflowConfigOptions): AiWorkflowConfig {
  const version = getPackageVersion();
  const now = new Date().toISOString();
  const primaryRuntime = runtimes.join("+");

  return {
    package: "@williambeto/ai-workflow",
    version,
    runtime: primaryRuntime,
    profile,
    installMode: "project-local",
    generatedAt: now,
    paths: {
      agents: ".ai-workflow/opencode/agents",
      commands: ".ai-workflow/opencode/commands",
      skills: ".ai-workflow/opencode/skills",
      policies: ".ai-workflow/opencode/docs/policies",
      harness: ".ai-workflow/harness",
      schemas: ".ai-workflow/schemas"
    }
  };
}
